import React, { useState } from 'react'
import Modal from 'antd/lib/modal'
import styled from '@emotion/styled'
import {
  Container,
  Description,
  Label,
  Space,
  Radio
} from 'components/Common/Form'
import { CommonButton } from 'components/Common'
import { AvailabilityAction, AvailabilityGroupScope } from './types'

interface AvailabilityGroupModalProps {
  visible: boolean
  onCancel: () => void
  onConfirm: (scope: AvailabilityGroupScope) => void
  itemName: string
  action: AvailabilityAction | null
}

const getActionText = (action: AvailabilityAction | null, itemName: string) => {
  switch (action) {
    case AvailabilityAction.OUT_OF_STOCK:
      return {
        title: 'Mark product out of stock',
        summary: `You're marking ${itemName} as out of stock`,
        action: 'Mark out of stock'
      }
    case AvailabilityAction.IN_STOCK:
      return {
        title: 'Mark product in stock',
        summary: `You're marking ${itemName} as in stock`,
        action: 'Mark in stock'
      }
    case AvailabilityAction.SNOOZE:
      return {
        title: 'Snooze product',
        summary: `You're about to snooze ${itemName}`,
        action: 'Snooze'
      }
    case AvailabilityAction.UNSNOOZE:
      return {
        title: 'Unsnooze product',
        summary: `You're about to unsnooze ${itemName}`,
        action: 'Unsnooze'
      }
    default:
      return {
        title: 'Mark product',
        summary: `You're about to apply this change to ${itemName}`,
        action: 'Apply'
      }
  }
}

const AvailabilityGroupModal = ({
  visible,
  onCancel,
  onConfirm,
  itemName,
  action
}: AvailabilityGroupModalProps) => {
  const [scope, setScope] = useState<AvailabilityGroupScope>(
    AvailabilityGroupScope.GROUP
  )
  const actionText = getActionText(action, itemName)

  return (
    <StyledModal
      open={visible}
      title={null}
      closable={false}
      onCancel={onCancel}
      footer={null}
    >
      <Title>{actionText.title}</Title>

      <Container isActive>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Label>{actionText.summary}</Label>
          <Description>
            This product belongs to an availability group. Would you like to
            mark only this product out of stock, or all products in the group?
          </Description>
        </div>

        <Space direction='vertical' align='start' gap={8}>
          <Radio
            checked={scope === AvailabilityGroupScope.GROUP}
            onClick={() => setScope(AvailabilityGroupScope.GROUP)}
          >
            <strong>All products in this group</strong>
            <Description>
              Applies the change to every product in the availability group.
            </Description>
          </Radio>
          <Radio
            checked={scope === AvailabilityGroupScope.SINGLE}
            onClick={() => setScope(AvailabilityGroupScope.SINGLE)}
          >
            <strong>Only this product</strong>
            <Description>Applies the change to this product only.</Description>
          </Radio>
        </Space>
      </Container>

      <ButtonRow>
        <CommonButton onClick={onCancel}>CANCEL</CommonButton>
        <CommonButton onClick={() => onConfirm(scope)} active={false}>
          {actionText.action}
        </CommonButton>
      </ButtonRow>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 0;
    overflow: hidden;
  }

  .ant-modal-header {
    display: none;
  }

  .ant-modal-body {
    padding: 16px;
  }
`

const Title = styled.h2`
  font-size: 18px;
  font-weight: 500;
  color: #4a4a68;
  margin-bottom: 24px;
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 12px;
  margin-top: 24px;
  border-top: 1px solid #e7e5e9;
`

export default AvailabilityGroupModal
